import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { BadgeCheck, CircleAlert, Fingerprint, Loader2, Lock, RefreshCw, ShieldQuestion } from 'lucide-react';
import {
  integrationIdentityService,
  type IntegrationCheckView,
  type IntegrationIdentityDetails,
} from '@/lib/integrationIdentityService';

interface Props {
  companyId: string;
  integrationId: string;
  providerName: string;
  variant?: 'full' | 'compact';
  autoCheck?: boolean;
}

const fmt = (iso: string) =>
  new Date(iso).toLocaleString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

const text = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : typeof v === 'number' ? String(v) : null);

function identityRows(provider: string, id: IntegrationIdentityDetails): Array<[string, string | null]> {
  const d = id.details ?? {};
  if (provider === 'rivhit') {
    return [
      ['שם העסק', id.account_name],
      ['מספר חברה ברווחית', id.account_ref],
      ['מנהל', text(d.manager_name)],
      ['עיר', text(d.city)],
    ];
  }
  if (provider === 'shopify' || provider === 'shopify_manual') {
    return [
      ['שם החנות', id.account_name],
      ['כתובת החנות', id.account_ref],
      ['דומיין ראשי', text(d.domain)],
      ['בעל החנות', text(d.shop_owner)],
      ['אימייל החנות', text(d.email)],
      ['מדינה', text(d.country)],
    ];
  }
  if (provider === 'financy') {
    return [
      ['מזהה משתמש ב-Financy', id.account_ref],
      ['חשבונות מחוברים', text(d.accounts_count)],
    ];
  }
  if (provider === 'tranzila') return [['שם המסוף בטרנזילה', id.account_name]];
  return [['חשבון', id.account_name ?? id.account_ref]];
}

function summaryLine(provider: string, id: IntegrationIdentityDetails): string {
  if (provider === 'rivhit') return [id.account_name, id.account_ref && `מספר חברה ${id.account_ref}`].filter(Boolean).join(' · ');
  if (provider === 'tranzila') return `מסוף ${id.account_name ?? ''}`;
  if (provider === 'financy') return `משתמש ${id.account_ref ?? ''} · ${text(id.details?.accounts_count) ?? 0} חשבונות`;
  return [id.account_name, id.account_ref].filter(Boolean).join(' · ');
}

interface FinancyAccount { name: string | null; bank: string | null; branch: string | null; number_last4: string | null }

export function ConnectionIdentityCard({ companyId, integrationId, providerName, variant = 'full', autoCheck = false }: Props) {
  const [view, setView] = useState<IntegrationCheckView | null>(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const autoChecked = useRef(false);

  const runCheck = useCallback(async () => {
    setChecking(true);
    setLoadError(null);
    try {
      const res = await integrationIdentityService.runCheck(integrationId);
      setView({
        canViewDetails: res.can_view_details,
        check: { status: res.status, error_message: res.error_message, verified_at: res.verified_at, identity: res.identity },
      });
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'הבדיקה נכשלה');
    } finally {
      setChecking(false);
    }
  }, [integrationId]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    let loaded: IntegrationCheckView | null = null;
    try {
      loaded = await integrationIdentityService.load(companyId, integrationId);
      setView(loaded);
    } catch (e) {
      console.error('Failed to load connection identity:', e);
      setLoadError('לא הצלחנו לטעון את מצב החיבור');
    } finally {
      setLoading(false);
    }
    if (autoCheck && loaded && !loaded.check && !autoChecked.current) {
      autoChecked.current = true;
      void runCheck();
    }
  }, [companyId, integrationId, autoCheck, runCheck]);

  useEffect(() => { void load(); }, [load]);

  const checkButton = (
    <button
      type="button"
      onClick={runCheck}
      disabled={checking || loading}
      className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
    >
      {checking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
      {checking ? 'בודק…' : view?.check ? 'בדוק שוב' : variant === 'compact' ? 'בדוק עכשיו' : 'בדוק חיבור'}
    </button>
  );

  if (loading) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-xs text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" /> טוען מצב חיבור…
      </div>
    );
  }

  const check = view?.check ?? null;

  if (!check && checking) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-xs text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" /> בודק חיבור…
      </div>
    );
  }
  const ownerNotice = (
    <p className="flex items-start gap-1.5 text-xs text-gray-600">
      <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      פרטי החשבון המחובר זמינים לבעל החברה בלבד. לקבלת המידע פנו לבעל החברה.
    </p>
  );

  let body: ReactNode;
  let tone: string;

  if (!check) {
    tone = 'border-gray-200 bg-gray-50';
    body = (
      <div className="flex items-start gap-2 text-sm text-gray-700">
        <ShieldQuestion className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" />
        <span>החיבור עוד לא נבדק. לחצו על "{variant === 'compact' ? 'בדוק עכשיו' : 'בדוק חיבור'}" כדי לוודא לאיזה חשבון הוא מחובר.</span>
      </div>
    );
  } else if (check.status === 'error') {
    tone = 'border-red-200 bg-red-50';
    body = (
      <div className="flex items-start gap-2 text-sm text-red-800">
        <CircleAlert className="mt-0.5 h-4 w-4 shrink-0" />
        <div>
          <p className="font-semibold">החיבור לא תקין</p>
          {check.error_message && <p className="mt-0.5 text-red-700">{check.error_message}</p>}
        </div>
      </div>
    );
  } else if (!view?.canViewDetails || !check.identity) {
    tone = 'border-emerald-200 bg-emerald-50';
    body = (
      <div className="space-y-1.5">
        <p className="flex items-center gap-2 text-sm font-semibold text-emerald-800">
          <BadgeCheck className="h-4 w-4" /> החיבור תקין
        </p>
        {ownerNotice}
      </div>
    );
  } else if (variant === 'compact') {
    tone = 'border-emerald-200 bg-emerald-50';
    body = (
      <div className="space-y-1">
        <p className="flex items-center gap-2 text-sm text-emerald-800">
          <BadgeCheck className="h-4 w-4 shrink-0" />
          <span>{providerName.startsWith('shopify') ? 'מחובר לחנות' : 'מחובר לחשבון'}: <strong className="font-semibold">{summaryLine(providerName, check.identity)}</strong></span>
        </p>
        {check.identity.key_hint && (
          <p className="flex items-center gap-1.5 text-xs text-gray-600">
            <Fingerprint className="h-3.5 w-3.5" /> מפתח: <span dir="ltr" className="font-mono">{check.identity.key_hint}</span>
          </p>
        )}
      </div>
    );
  } else {
    tone = 'border-emerald-200 bg-emerald-50';
    const rows = identityRows(providerName, check.identity).filter(([, v]) => v);
    const financyAccounts = providerName === 'financy'
      ? ((check.identity.details?.accounts ?? []) as FinancyAccount[])
      : [];
    body = (
      <div className="space-y-3">
        <p className="flex items-center gap-2 text-sm font-semibold text-emerald-800">
          <BadgeCheck className="h-4 w-4" /> מחובר לחשבון הבא
        </p>
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
          {rows.map(([label, value]) => (
            <div key={label} className="contents">
              <dt className="text-gray-600">{label}</dt>
              <dd className="font-medium text-gray-900 break-all">{value}</dd>
            </div>
          ))}
          {check.identity.key_hint && (
            <div className="contents">
              <dt className="flex items-center gap-1 text-gray-600"><Fingerprint className="h-3.5 w-3.5" />טביעת אצבע של המפתח</dt>
              <dd dir="ltr" className="text-right font-mono text-gray-900">{check.identity.key_hint}</dd>
            </div>
          )}
        </dl>
        {financyAccounts.length > 0 && (
          <ul className="space-y-1 rounded-lg bg-white/70 p-2 text-xs text-gray-700">
            {financyAccounts.map((a, i) => (
              <li key={i}>
                {[a.name, a.bank && `בנק ${a.bank}`, a.branch && `סניף ${a.branch}`, a.number_last4 && `חשבון ••${a.number_last4}`]
                  .filter(Boolean).join(' · ')}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  return (
    <div className={`rounded-xl border px-4 py-3 transition-colors ${tone}`}>
      {body}
      {loadError && <p className="mt-2 text-xs text-red-700">{loadError}</p>}
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs text-gray-500">
          {check ? `נבדק לאחרונה: ${fmt(check.verified_at)}` : ''}
        </span>
        {checkButton}
      </div>
    </div>
  );
}
